// Catálogo das áreas de novidade (spec 2026-10-27-novidades-por-perfil §5.1).
//
// Uma área é um GRUPO do menu (Sidebar.tsx, `SECTIONS`), não uma tela: quem tem qualquer
// permissão de `requer` (em qualquer tenant) ou de `requerNaPlataforma` (lista GLOBAL,
// avaliada com `podeNaPlataforma`, como a Sidebar faz em Frota, Certificações e SSO)
// recebe as novidades dela. `criterio` nomeia o booleano que a Sidebar
// calcula para telas decididas por `hidden:` calculado. Sem tela no menu (ambientes,
// terraform), `requer` é a permissão que o SERVIDOR exige para a ação.
// `rota` liga a área a uma tela da Sidebar e é conferida por teste de coerência.
// Telas sem guarda (Aplicativos, Notificações, Alertas, Conversas) não são área: `geral`.

export type ChangelogItem = string | { text: string; areas: string[] }

export const AREA_GERAL = 'geral'

export function textoDoItem(i: ChangelogItem): string {
  return typeof i === 'string' ? i : i.text
}

export function areasDoItem(i: ChangelogItem): string[] {
  return typeof i === 'string' ? [AREA_GERAL] : i.areas
}

export type CriterioDeArea =
  | 'organizacoes'
  | 'gatewaysWhatsapp'
  | 'meuRobo'
  | 'pedidosDeAnalise'
  | 'baseDeConhecimento'
  | 'dashboard'
  | 'mensagensDoSistema'
  | 'conversasWhatsapi'

export interface AreaDeNovidade {
  chave: string
  rotulo: string
  /** Qualquer uma das permissões basta (OR), em qualquer tenant (`can`). */
  requer?: string[]
  /** Permissões de PLATAFORMA (OR), avaliadas com `podeNaPlataforma`, nunca com `can`. */
  requerNaPlataforma?: string[]
  /** Booleano que a Sidebar já calcula para a tela. */
  criterio?: CriterioDeArea
  /** Tela da Sidebar que a área representa. */
  rota?: string
}

export const AREAS: AreaDeNovidade[] = [
  // Operacional
  { chave: 'dashboard', rotulo: 'Dashboard', criterio: 'dashboard', rota: '/' },
  { chave: 'base-de-conhecimento', rotulo: 'Base de Conhecimento', criterio: 'baseDeConhecimento', rota: '/conhecimento' },
  { chave: 'recursos', rotulo: 'Recursos', requer: ['recursos:read'], rota: '/recursos' },
  { chave: 'servidores', rotulo: 'Servidores', requer: ['ssm:session:dev:access'], rota: '/ssm' },
  {
    chave: 'chamados',
    rotulo: 'Chamados',
    requer: ['chamados:read', 'chamados:gmud_read', 'ccm:manage'],
    rota: '/chamados',
  },
  { chave: 'banco-de-dados', rotulo: 'Banco de Dados', requer: ['rds:read'], rota: '/database' },
  { chave: 'enderecamento-ip', rotulo: 'Endereçamento IP', requer: ['ipam:view'], rota: '/enderecamento' },
  { chave: 'rotinas', rotulo: 'Rotinas', requer: ['routines:manage'], rota: '/rotinas' },
  {
    chave: 'auditoria',
    rotulo: 'Auditoria',
    requer: ['audit:read'],
    requerNaPlataforma: ['certificacoes:read'],
    rota: '/auditoria',
  },
  // FinOps
  {
    chave: 'finops',
    rotulo: 'FinOps',
    requer: ['finops:read', 'finops:export', 'admin:platform'],
    rota: '/finops',
  },
  // Segurança
  { chave: 'seguranca', rotulo: 'Segurança', requer: ['security:read'], rota: '/security' },
  { chave: 'waf', rotulo: 'WAF', requer: ['waf:view'], rota: '/security/waf' },
  {
    chave: 'ztna',
    rotulo: 'ZTNA',
    requer: ['ztna:onboard:provision', 'connectors:manage', 'broker:revoke_session', 'ztna_customer:read'],
    rota: '/admin/ztna',
  },
  { chave: 'cloudhsm', rotulo: 'CloudHSM', requer: ['cloudhsm:view'], rota: '/security/cloudhsm' },
  // Integrações
  { chave: 'jira', rotulo: 'Jira', requer: ['jira:read', 'jira:admin'], rota: '/integracoes/jira' },
  { chave: 'whatsapi', rotulo: 'WhatsApi', requer: ['whatsapi:read', 'whatsapi:observe'], rota: '/whatsapi/messages' },
  // Robô
  { chave: 'meu-robo', rotulo: 'Meu robô', criterio: 'meuRobo', rota: '/robo/meu' },
  { chave: 'frota-de-robos', rotulo: 'Frota de robôs', requerNaPlataforma: ['robo:read'], rota: '/robo/frota' },
  { chave: 'pedidos-de-analise', rotulo: 'Pedidos de análise', criterio: 'pedidosDeAnalise', rota: '/robo/pedidos' },
  // Administração
  {
    chave: 'administracao',
    rotulo: 'Administração',
    // A Sidebar usa os dois: `permission: 'admin:platform'` (Tenants, Funções...) e
    // `podeNaPlataforma(user, 'admin', 'platform')` (SSO).
    requer: ['usuarios:manage', 'admin:platform', 'ldap:manage', 'alerts:manage'],
    requerNaPlataforma: ['admin:platform'],
    rota: '/admin/usuarios',
  },
  { chave: 'organizacoes', rotulo: 'Organizações', criterio: 'organizacoes', rota: '/admin/organizacoes' },
  {
    chave: 'gateways-whatsapp',
    rotulo: 'Gateways WhatsApp',
    criterio: 'gatewaysWhatsapp',
    rota: '/admin/whatsapp-gateways',
  },
  {
    chave: 'mensagens-do-sistema',
    rotulo: 'Mensagens do sistema',
    criterio: 'mensagensDoSistema',
    rota: '/admin/notificacoes',
  },
  // Sem tela no menu: a permissão é a que o servidor exige (chat_actions.py)
  { chave: 'ambientes', rotulo: 'Ambientes novos', requer: ['terraform:criar_ambiente'] },
  { chave: 'terraform', rotulo: 'Terraform', requer: ['terraform:adjust'] },
]
